import moment from "moment";
import {
  NewPageWrapper,
  NewPageWrapperCopy,
} from "../../../../ui-components/custom-components";
import { useCallback, useEffect, useState } from "react";
import {
  IUserServiceFee,
  IUserServiceFeeTranche,
} from "../../user-info/user-info";
import { useEthosNotification } from "../../../../contexts/ethos-notification-context";
import { useUserFee } from "../../../../contexts/user-fee-context";

interface UserInfoModalProps {
  closeModal: (value: boolean) => void;
  serviceFee: IUserServiceFee;
  isAdmin: boolean;
  onSaved?: (id: string) => void;
}

const UserInfoModal = ({
  closeModal,
  serviceFee,
  isAdmin,
  onSaved,
}: UserInfoModalProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [localTranches, setLocalTranches] = useState<IUserServiceFeeTranche[]>(
    serviceFee.arrayTranche ?? [],
  );

  const { handleError } = useEthosNotification();
  const { patchFeeUserById } = useUserFee();

  const handleTogglePaid = useCallback((index: number, nextPaid: boolean) => {
    setLocalTranches((prev) =>
      prev.map((t, i) =>
        i === index
          ? {
              ...t,
              paid: nextPaid,
              settlementDate: nextPaid ? new Date() : null,
            }
          : t,
      ),
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (!isAdmin) {
      return;
    }

    try {
      setIsUpdating(true);

      const payload = {
        ...serviceFee,
        arrayTranche: localTranches,
      };

      const { error, data } = await patchFeeUserById(serviceFee._id, payload);

      if (data?.error) {
        handleError(error);
        return;
      }

      onSaved?.(serviceFee.userId);
      closeModal(false);
    } finally {
      setIsUpdating(false);
    }
  }, [
    closeModal,
    handleError,
    isAdmin,
    localTranches,
    onSaved,
    patchFeeUserById,
    serviceFee,
  ]);

  useEffect(() => {
    setLocalTranches(serviceFee.arrayTranche ?? []);
  }, [serviceFee]);

  return (
    <NewPageWrapper>
      <NewPageWrapperCopy>
        <div className="top-page">
          <h3 className="top-page-title">Dettagli sul pagamenti</h3>
          <div className="d-flex gap-2">
            <button
              className="btn btn-secondary"
              disabled={isUpdating}
              onClick={() => closeModal(false)}
            >
              Chiudi
            </button>

            {isAdmin && (
              <button
                className="btn btn-primary"
                disabled={isUpdating}
                onClick={handleSave}
              >
                Salva
              </button>
            )}
          </div>
        </div>

        <div className="d-flex gap-2 my-3 align-items-center">
          <table className="table table-striped">
            <thead className="thead-dark">
              <tr>
                <th>Tranche</th>
                <th>Percentuale</th>
                <th>Importo</th>
                <th>Commissione</th>
                <th>Data pagamento</th>
                <th>Data liquidazione</th>
                <th>Pagato</th>
              </tr>
            </thead>
            <tbody>
              {localTranches && localTranches.length > 0 ? (
                localTranches.map((item, index) => (
                  <tr key={index}>
                    <td>#{index + 1}</td>
                    <td>{item.percentage}%</td>
                    <td>{item.amount.toFixed(2)}€</td>
                    <td>{item.fee.toFixed(2)}€</td>
                    <td>
                      {item.paymentDate
                        ? moment(item.paymentDate).format("DD/MM/YYYY")
                        : "N/D"}
                    </td>
                    <td>
                      {item.settlementDate
                        ? moment(item.settlementDate).format("DD/MM/YYYY")
                        : "N/D"}
                    </td>
                    <td>
                      <div className="form-check form-switch">
                        <input
                          id={`flexSwitchCheck_${index}`}
                          aria-label="check-box"
                          style={{ float: "none" }}
                          className="form-check-input"
                          type="checkbox"
                          disabled={isUpdating || !isAdmin}
                          checked={item.paid}
                          onChange={(e) =>
                            handleTogglePaid(index, e.target.checked)
                          }
                        />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <th colSpan={4} style={{ textAlign: "center" }}>
                    Non ci sono tranche collegate a questo servizio
                  </th>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </NewPageWrapperCopy>
    </NewPageWrapper>
  );
};

export default UserInfoModal;
